/**
 * Simplified Customer Form - 2 Tabs Only
 * Easy customer creation with minimal required fields
 */

import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Box, Paper, Typography, TextField, Grid, FormControl, InputLabel,
  Select, MenuItem, Tabs, Tab, Autocomplete, Chip,
} from '@mui/material'
import {
  Save as SaveIcon, Person as PersonIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import {
  useCreateCustomerMutation, useUpdateCustomerMutation, useGetCustomerQuery,
  useGetCustomerGroupsQuery,
} from '../store/services/customerApi'
import { useGetCataloguesQuery } from '../store/services/catalogueApi'
import { CustomerCreateData, CustomerType } from '../types/customer'
import PageHeader from '../components/ui/PageHeader'
import AddressManagement from '../components/customers/AddressManagement'

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const CustomerFormSimple: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { enqueueSnackbar } = useSnackbar()
  const isEditing = Boolean(id)

  const [activeTab, setActiveTab] = useState(0)

  const [formData, setFormData] = useState<CustomerCreateData>({
    name: '',
    billing_name: '',
    email: '',
    mobile_no: '',
    phone: '',
    whatsapp_no: '',
    contact_person: '',
    contact_person_designation: '',
    contact_person_email: '',
    gstin: '',
    pan: '',
    is_gst_registered: false,
    customer_type: CustomerType.RETAIL,
    customer_group_id: '',
    credit_limit: 0,
    credit_days: 0,
    transport_option: 'Local Delivery',
    transport_details: {},
    assigned_catalogues: [],
    communication_preferences: {
      email_enabled: true,
      sms_enabled: true,
      whatsapp_enabled: false,
      notification_types: {
        order_updates: true,
        payment_reminders: true,
        promotional: false,
      },
    },
    meta: {},
  })

  // Optional address data (not part of customer creation, will be added separately if provided)
  const [billingAddress, setBillingAddress] = useState({
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    pincode: '',
    country: 'India',
  })

  const [shippingAddress, setShippingAddress] = useState({
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    pincode: '',
    country: 'India',
  })

  const [sameAsBilling, setSameAsBilling] = useState(true)

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)

  const { data: customer, isLoading: customerLoading } = useGetCustomerQuery(id!, { skip: !isEditing })
  const [createCustomer] = useCreateCustomerMutation()
  const [updateCustomer] = useUpdateCustomerMutation()
  const { data: groupsData } = useGetCustomerGroupsQuery({ page: 1, per_page: 100, is_active: true })
  const { data: cataloguesData } = useGetCataloguesQuery({ page: 1, per_page: 100 })

  // Auto-assign all catalogues when creating new customer
  useEffect(() => {
    if (!isEditing && cataloguesData?.items && (formData.assigned_catalogues || []).length === 0) {
      const allCatalogueIds = cataloguesData.items.map(cat => cat.id)
      setFormData(prev => ({
        ...prev,
        assigned_catalogues: allCatalogueIds
      }))
    }
  }, [cataloguesData, isEditing])

  useEffect(() => {
    if (customer) {
      setFormData({
        name: customer.name,
        billing_name: customer.billing_name,
        email: customer.email,
        mobile_no: customer.mobile_no,
        phone: customer.phone || '',
        whatsapp_no: customer.whatsapp_no || '',
        contact_person: customer.contact_person || '',
        contact_person_designation: customer.contact_person_designation || '',
        contact_person_email: customer.contact_person_email || '',
        gstin: customer.gstin || '',
        pan: customer.pan || '',
        is_gst_registered: customer.is_gst_registered,
        customer_type: customer.customer_type,
        customer_group_id: customer.customer_group_id,
        credit_limit: customer.credit_limit,
        credit_days: customer.credit_days,
        transport_option: customer.transport_option || 'Local Delivery',
        transport_details: customer.transport_details || {},
        assigned_catalogues: customer.assigned_catalogues || [],
        communication_preferences: customer.communication_preferences || formData.communication_preferences,
        meta: customer.meta || {},
      })
    }
  }, [customer])

  const handleInputChange = (field: keyof CustomerCreateData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }))
  }

  const handleBillingAddressChange = (field: string, value: string) => {
    setBillingAddress(prev => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleShippingAddressChange = (field: string, value: string) => {
    setShippingAddress(prev => ({
      ...prev,
      [field]: value,
    }))
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    // Required fields
    if (!formData.name.trim()) newErrors.name = 'Name is required'
    if (!formData.billing_name.trim()) newErrors.billing_name = 'Billing name is required'
    if (!formData.email.trim()) newErrors.email = 'Email is required'
    if (!formData.mobile_no.trim()) newErrors.mobile_no = 'Mobile number is required'
    if (!formData.customer_group_id) newErrors.customer_group_id = 'Customer group is required'

    // Mobile number validation
    if (formData.mobile_no && !/^[6-9]\d{9}$/.test(formData.mobile_no)) {
      newErrors.mobile_no = 'Invalid mobile number'
    }

    // WhatsApp validation (required)
    if (!formData.whatsapp_no) {
      newErrors.whatsapp_no = 'WhatsApp number is required'
    } else if (!/^[6-9]\d{9}$/.test(formData.whatsapp_no)) {
      newErrors.whatsapp_no = 'Invalid WhatsApp number'
    }

    // Email validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email address'
    }

    // GST validation - if GST registered, GSTIN and PAN are required
    if (formData.is_gst_registered) {
      if (!formData.gstin || !formData.gstin.trim()) {
        newErrors.gstin = 'GSTIN is required for GST registered customers'
      } else if (!/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/.test(formData.gstin)) {
        newErrors.gstin = 'Invalid GSTIN format (e.g., 22AAAAA0000A1Z5)'
      }

      if (!formData.pan || !formData.pan.trim()) {
        newErrors.pan = 'PAN is required for GST registered customers'
      } else if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.pan)) {
        newErrors.pan = 'Invalid PAN format (e.g., AAAAA0000A)'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) {
      enqueueSnackbar('Please fix the errors in the form', { variant: 'error' })
      return
    }

    setLoading(true)
    try {
      // Prepare customer data
      const customerPayload: any = { ...formData }
      
      // Add billing address if any field is filled
      const hasBillingAddress = billingAddress.address_line1 || billingAddress.city || billingAddress.state || billingAddress.pincode
      if (hasBillingAddress) {
        customerPayload.billing_address = billingAddress
      }

      // Add shipping address if different from billing
      if (hasBillingAddress && !sameAsBilling) {
        const hasShippingAddress = shippingAddress.address_line1 || shippingAddress.city || shippingAddress.state || shippingAddress.pincode
        if (hasShippingAddress) {
          customerPayload.shipping_addresses = [shippingAddress]
        }
      }

      if (isEditing) {
        await updateCustomer({ id: id!, data: customerPayload }).unwrap()
        enqueueSnackbar('Customer updated successfully', { variant: 'success' })
      } else {
        await createCustomer(customerPayload).unwrap()
        enqueueSnackbar('Customer created successfully', { variant: 'success' })
      }
      navigate('/admin/parties/customers')
    } catch (error: any) {
      enqueueSnackbar(error?.data?.detail || 'Failed to save customer', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  if (customerLoading) {
    return <Box sx={{ p: 3 }}><Typography>Loading...</Typography></Box>
  }

  return (
    <Box>
      <PageHeader
        title={isEditing ? 'Edit Customer' : 'Add Customer'}
        subtitle="Quick customer creation - address is optional"
        breadcrumbs={[
          { label: 'Party Management', path: '/admin/parties' },
          { label: 'Customers', path: '/admin/parties/customers' },
          { label: isEditing ? 'Edit' : 'Add', path: '' }
        ]}
        actions={[
          {
            label: loading ? 'Saving...' : 'Save Customer',
            onClick: handleSubmit,
            variant: 'contained' as const,
            icon: <SaveIcon />,
            disabled: loading,
          }
        ]}
      />

      <Paper sx={{ mt: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab icon={<PersonIcon />} label="Basic Information" iconPosition="start" />
          <Tab icon={<LocationIcon />} label="Address (Optional)" iconPosition="start" />
        </Tabs>

        {/* Tab 1: Basic Information */}
        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={3}>
            {/* Customer Name */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Customer Name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                error={Boolean(errors.name)}
                helperText={errors.name || 'Display name for the customer'}
              />
            </Grid>

            {/* Billing Name */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Billing Name"
                value={formData.billing_name}
                onChange={(e) => handleInputChange('billing_name', e.target.value)}
                error={Boolean(errors.billing_name)}
                helperText={errors.billing_name || 'Name to appear on invoices'}
              />
            </Grid>

            {/* Mobile Number */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Mobile Number"
                value={formData.mobile_no}
                onChange={(e) => handleInputChange('mobile_no', e.target.value.replace(/\D/g, '').slice(0, 10))}
                error={Boolean(errors.mobile_no)}
                helperText={errors.mobile_no || 'Primary contact number (10 digits)'}
                inputProps={{ maxLength: 10 }}
              />
            </Grid>

            {/* Email */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                type="email"
                label="Email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                error={Boolean(errors.email)}
                helperText={errors.email || 'Primary email address'}
              />
            </Grid>

            {/* WhatsApp Number (Required) */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="WhatsApp Number"
                value={formData.whatsapp_no}
                onChange={(e) => handleInputChange('whatsapp_no', e.target.value.replace(/\D/g, '').slice(0, 10))}
                error={Boolean(errors.whatsapp_no)}
                helperText={errors.whatsapp_no || 'Required for WhatsApp notifications and OTP'}
                inputProps={{ maxLength: 10 }}
              />
            </Grid>

            {/* Customer Type */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Customer Type</InputLabel>
                <Select
                  value={formData.customer_type}
                  onChange={(e) => handleInputChange('customer_type', e.target.value)}
                  label="Customer Type"
                >
                  <MenuItem value={CustomerType.RETAIL}>Retail</MenuItem>
                  <MenuItem value={CustomerType.RESELLER}>Reseller</MenuItem>
                  <MenuItem value={CustomerType.WHOLESALER}>Wholesaler</MenuItem>
                  <MenuItem value={CustomerType.BROKER}>Broker</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Determines pricing and product visibility
              </Typography>
            </Grid>

            {/* Customer Group */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required error={Boolean(errors.customer_group_id)}>
                <InputLabel>Customer Group</InputLabel>
                <Select
                  value={formData.customer_group_id}
                  onChange={(e) => handleInputChange('customer_group_id', e.target.value)}
                  label="Customer Group"
                >
                  {groupsData?.items.map((group) => (
                    <MenuItem key={group.id} value={group.id}>
                      {group.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Applies group-specific rules and pricing
              </Typography>
            </Grid>

            {/* GST Registration Status */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>GST Registration Status</InputLabel>
                <Select
                  value={formData.is_gst_registered ? 'yes' : 'no'}
                  onChange={(e) => {
                    const isRegistered = e.target.value === 'yes'
                    handleInputChange('is_gst_registered', isRegistered)
                    // Clear GST fields if not registered
                    if (!isRegistered) {
                      handleInputChange('gstin', '')
                      handleInputChange('pan', '')
                    }
                  }}
                  label="GST Registration Status"
                >
                  <MenuItem value="no">Not GST Registered</MenuItem>
                  <MenuItem value="yes">GST Registered</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Select if customer has GST registration
              </Typography>
            </Grid>

            {/* GSTIN - Show only if GST registered */}
            {formData.is_gst_registered && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="GSTIN"
                  value={formData.gstin}
                  onChange={(e) => handleInputChange('gstin', e.target.value.toUpperCase())}
                  error={Boolean(errors.gstin)}
                  helperText={errors.gstin || 'GST Identification Number (15 characters)'}
                  inputProps={{ maxLength: 15 }}
                  placeholder="22AAAAA0000A1Z5"
                />
              </Grid>
            )}

            {/* PAN - Show only if GST registered */}
            {formData.is_gst_registered && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="PAN"
                  value={formData.pan}
                  onChange={(e) => handleInputChange('pan', e.target.value.toUpperCase())}
                  error={Boolean(errors.pan)}
                  helperText={errors.pan || 'Permanent Account Number (10 characters)'}
                  inputProps={{ maxLength: 10 }}
                  placeholder="AAAAA0000A"
                />
              </Grid>
            )}

            {/* Credit Limit */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Credit Limit"
                value={formData.credit_limit}
                onChange={(e) => handleInputChange('credit_limit', parseFloat(e.target.value) || 0)}
                helperText="Maximum credit amount allowed"
              />
            </Grid>

            {/* Credit Days */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Credit Days"
                value={formData.credit_days}
                onChange={(e) => handleInputChange('credit_days', parseInt(e.target.value) || 0)}
                helperText="Payment due days (0 = immediate payment)"
              />
            </Grid>

            {/* Assigned Catalogues */}
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={cataloguesData?.items || []}
                getOptionLabel={(option) => option.name}
                value={cataloguesData?.items.filter((cat) => (formData.assigned_catalogues || []).includes(cat.id)) || []}
                onChange={(_, newValue) => {
                  handleInputChange('assigned_catalogues', newValue.map((cat) => cat.id))
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Assigned Catalogues" 
                    helperText="All catalogues are assigned by default. Remove any you don't want this customer to see." 
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip label={option.name} {...getTagProps({ index })} />
                  ))
                }
              />
            </Grid>
          </Grid>
        </TabPanel>

        {/* Tab 2: Address (Optional) */}
        <TabPanel value={activeTab} index={1}>
          {isEditing && customer ? (
            /* Show AddressManagement component when editing existing customer */
            <AddressManagement 
              customerId={customer.id} 
              customerName={customer.name}
            />
          ) : (
            /* Show simple address form when creating new customer */
            <>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Address information is optional. You can add it now or later. Shipping address defaults to billing address if not specified.
                </Typography>
              </Box>
              
              <Grid container spacing={3}>
                {/* Billing Address Section */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationIcon color="primary" />
                    Billing Address (Bill To)
                  </Typography>
                </Grid>

                {/* Billing Address Line 1 */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address Line 1"
                    value={billingAddress.address_line1}
                    onChange={(e) => handleBillingAddressChange('address_line1', e.target.value)}
                    helperText="Street address, building name, etc."
                  />
                </Grid>

                {/* Billing Address Line 2 */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address Line 2 (Optional)"
                    value={billingAddress.address_line2}
                    onChange={(e) => handleBillingAddressChange('address_line2', e.target.value)}
                    helperText="Apartment, suite, unit, etc."
                  />
                </Grid>

                {/* Billing City */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="City"
                    value={billingAddress.city}
                    onChange={(e) => handleBillingAddressChange('city', e.target.value)}
                  />
                </Grid>

                {/* Billing State */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="State"
                    value={billingAddress.state}
                    onChange={(e) => handleBillingAddressChange('state', e.target.value)}
                  />
                </Grid>

                {/* Billing Pincode */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Pincode"
                    value={billingAddress.pincode}
                    onChange={(e) => handleBillingAddressChange('pincode', e.target.value.replace(/\D/g, '').slice(0, 6))}
                    inputProps={{ maxLength: 6 }}
                    helperText="6-digit postal code"
                  />
                </Grid>

                {/* Divider */}
                <Grid item xs={12}>
                  <Box sx={{ my: 2, borderTop: 1, borderColor: 'divider' }} />
                </Grid>

                {/* Shipping Address Section */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationIcon color="secondary" />
                      Shipping Address (Ship To)
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <input
                        type="checkbox"
                        id="sameAsBilling"
                        checked={sameAsBilling}
                        onChange={(e) => setSameAsBilling(e.target.checked)}
                        style={{ cursor: 'pointer' }}
                      />
                      <label htmlFor="sameAsBilling" style={{ cursor: 'pointer', userSelect: 'none' }}>
                        Same as Billing Address
                      </label>
                    </Box>
                  </Box>
                </Grid>

                {!sameAsBilling && (
                  <>
                    {/* Shipping Address Line 1 */}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Address Line 1"
                        value={shippingAddress.address_line1}
                        onChange={(e) => handleShippingAddressChange('address_line1', e.target.value)}
                        helperText="Street address, building name, etc."
                      />
                    </Grid>

                    {/* Shipping Address Line 2 */}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Address Line 2 (Optional)"
                        value={shippingAddress.address_line2}
                        onChange={(e) => handleShippingAddressChange('address_line2', e.target.value)}
                        helperText="Apartment, suite, unit, etc."
                      />
                    </Grid>

                    {/* Shipping City */}
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="City"
                        value={shippingAddress.city}
                        onChange={(e) => handleShippingAddressChange('city', e.target.value)}
                      />
                    </Grid>

                    {/* Shipping State */}
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="State"
                        value={shippingAddress.state}
                        onChange={(e) => handleShippingAddressChange('state', e.target.value)}
                      />
                    </Grid>

                    {/* Shipping Pincode */}
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Pincode"
                        value={shippingAddress.pincode}
                        onChange={(e) => handleShippingAddressChange('pincode', e.target.value.replace(/\D/g, '').slice(0, 6))}
                        inputProps={{ maxLength: 6 }}
                        helperText="6-digit postal code"
                      />
                    </Grid>
                  </>
                )}

                {/* Divider */}
                <Grid item xs={12}>
                  <Box sx={{ my: 2, borderTop: 1, borderColor: 'divider' }} />
                </Grid>

                {/* Transport Option */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Delivery Method</InputLabel>
                    <Select
                      value={formData.transport_option}
                      onChange={(e) => handleInputChange('transport_option', e.target.value)}
                      label="Delivery Method"
                    >
                      <MenuItem value="Local Delivery">Local Delivery</MenuItem>
                      <MenuItem value="Via Bus">Via Bus</MenuItem>
                      <MenuItem value="Via Transport">Via Transport</MenuItem>
                      <MenuItem value="Via Courier">Via Courier</MenuItem>
                      <MenuItem value="Self Pickup">Self Pickup</MenuItem>
                    </Select>
                  </FormControl>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Preferred delivery method for orders
                  </Typography>
                </Grid>
              </Grid>
            </>
          )}
        </TabPanel>
      </Paper>
    </Box>
  )
}

export default CustomerFormSimple
